// next-app/src/hooks/use-events.ts
'use client';

import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth-store';
import type {
  EFGEvent,
  EligibilityCheckResponse,
  EventResponse,
  EventsListResponse,
} from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { queryKeys } from './query-keys';

// ============================================================================
// Events List Query
// ============================================================================

interface UseEventsOptions {
  /** Enable/disable the query */
  enabled?: boolean;
}

/**
 * Hook to fetch all events for the current dealer.
 *
 * Replaces the pattern:
 * ```
 * const { response, result } = await dtg.getEvents();
 * if (response.ok) {
 *   ui.setEvents(result.events);
 * }
 * ```
 *
 * Usage:
 * ```tsx
 * const { data, isLoading, error } = useEvents();
 * // data?.events is the events array
 * // isLoading replaces uiStore.isGettingEvents
 * ```
 */
export function useEvents(options: UseEventsOptions = {}) {
  const token = useAuthStore((state) => state.dealerToken);

  return useQuery({
    queryKey: queryKeys.events.lists(),
    queryFn: async () => {
      const { response, result } = await api.getEvents();

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      // API returns a flat array, normalize to EventsListResponse shape
      const events = Array.isArray(result) ? result : (result?.events ?? []);
      return { events } as EventsListResponse;
    },
    // Only fetch if authenticated
    enabled: options.enabled !== false && Boolean(token),
    // Events change frequently during active use
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Selector to get events array from query result.
 * Provides empty array fallback.
 */
export function useEventsList() {
  const { data, ...rest } = useEvents();
  return {
    ...rest,
    events: data?.events ?? [],
  };
}

// ============================================================================
// Single Event Query
// ============================================================================

interface UseEventOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch a single event by ID.
 *
 * Original: GET v1/event/request/{id}
 *
 * Usage:
 * ```tsx
 * const { data, isLoading } = useEvent(eventId);
 * // data?.event is the event object
 * ```
 */
export function useEvent(
  eventRequestId: string | number | undefined,
  options: UseEventOptions = {}
) {
  const token = useAuthStore((state) => state.dealerToken);

  return useQuery({
    queryKey: queryKeys.events.detail(eventRequestId ?? ''),
    queryFn: async () => {
      if (!eventRequestId) {
        throw new Error('Event ID is required');
      }

      const { response, result } = await api.getEventRequest(eventRequestId);

      if (!response.ok) {
        throw new Error('Failed to fetch event');
      }

      return result as EventResponse;
    },
    enabled: options.enabled !== false && Boolean(token) && Boolean(eventRequestId),
  });
}

// ============================================================================
// Eligibility Check Query
// ============================================================================

/**
 * Hook to check if dealer is eligible to create a new event.
 *
 * Original: GET v1/event/check-request
 * Returns: { eligible: boolean, reason?: string }
 */
export function useEligibilityCheck() {
  const token = useAuthStore((state) => state.dealerToken);

  return useQuery({
    queryKey: queryKeys.events.eligibility(),
    queryFn: async () => {
      const { response, result } = await api.checkRequest();

      if (!response.ok) {
        throw new Error('Failed to check eligibility');
      }

      return result as EligibilityCheckResponse;
    },
    enabled: Boolean(token),
    // Don't refetch too often - eligibility doesn't change frequently
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// Create Event Mutation
// ============================================================================

interface UseCreateEventOptions {
  onSuccess?: (event: EFGEvent) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to create a new event request (eligibility form submission).
 *
 * Original: POST v1/event/create-request
 *
 * The request date is automatically adjusted to noon UTC to avoid
 * timezone issues (matching original behavior).
 */
export function useCreateEvent(options: UseCreateEventOptions = {}) {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventRequest: Partial<EFGEvent>) => {
      const { response, result } = await api.saveRequest(eventRequest);

      if (!response.ok) {
        const errorResult = result as { message?: string };
        throw new Error(errorResult?.message || 'Failed to create event');
      }

      return result as EventResponse;
    },
    onSuccess: (data) => {
      // Invalidate events list to show new event
      queryClient.invalidateQueries({ queryKey: queryKeys.events.lists() });

      // Invalidate eligibility since it may have changed
      queryClient.invalidateQueries({ queryKey: queryKeys.events.eligibility() });

      // Navigate to event planning form
      if (data.event?.event_request_id) {
        router.push(`/event/${data.event.event_request_id}`);
      }

      options.onSuccess?.(data.event);
    },
    onError: (error: Error) => {
      console.error('[useCreateEvent] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Save Event Plan Mutation
// ============================================================================

interface EventPlanData extends Record<string, unknown> {
  event_request_id: number;
  event_plan_file?: File;
}

interface UseSaveEventPlanOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to save event plan details (multipart form with file upload).
 *
 * Original: POST v1/event/event_request/{id}
 * Uses multipart/form-data for file upload.
 *
 * Sets status to 'planning' if current status is 'eligible' or 'revise'.
 */
export function useSaveEventPlan(options: UseSaveEventPlanOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventPlan: EventPlanData) => {
      const { response, result } = await api.saveEventPlan(eventPlan);

      if (!response.ok) {
        throw new Error('Failed to save event plan');
      }

      return result as EventResponse;
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific event
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.detail(variables.event_request_id),
      });

      // Invalidate events list (status may have changed)
      queryClient.invalidateQueries({ queryKey: queryKeys.events.lists() });

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useSaveEventPlan] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Submit Event for Review Mutation
// ============================================================================

interface UseSubmitEventOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to submit event for PHQ review.
 *
 * Original: POST v1/event/event_request/submit/{id}
 * Changes event status from 'planning' to 'review'.
 */
export function useSubmitEvent(options: UseSubmitEventOptions = {}) {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventRequestId: number) => {
      const { response, result } = await api.submitRequest(eventRequestId);

      if (!response.ok) {
        throw new Error('Failed to submit event for review');
      }

      return result as EventResponse;
    },
    onSuccess: (_, eventRequestId) => {
      // Invalidate the specific event
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.detail(eventRequestId),
      });

      // Invalidate events list
      queryClient.invalidateQueries({ queryKey: queryKeys.events.lists() });

      // Navigate back to events list
      router.push('/events');

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useSubmitEvent] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Save Reimbursement Mutation
// ============================================================================

interface ReimbursementData extends Record<string, unknown> {
  event_id: number;
}

interface UseSaveReimbursementOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to save reimbursement request (multipart form with file upload).
 *
 * Original: POST v1/event/event_reimbursement/{id}
 */
export function useSaveReimbursement(options: UseSaveReimbursementOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reimbursementInfo: ReimbursementData) => {
      const { response, result } = await api.saveReimbursement(reimbursementInfo);

      if (!response.ok) {
        throw new Error('Failed to save reimbursement');
      }

      return result;
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.detail(variables.event_id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.events.lists() });

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useSaveReimbursement] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Event Portal Token Mutation
// ============================================================================

interface UseEventTokenOptions {
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to create magic link token for event portal access.
 *
 * Original: POST v1/auth/create_login_token
 *
 * Returns a URL that can be used to access the event portal
 * without requiring login credentials.
 */
export function useEventToken(options: UseEventTokenOptions = {}) {
  return useMutation({
    mutationFn: async (eventId: string) => {
      const { response, result } = await api.getEventToken(eventId);

      if (!response.ok) {
        throw new Error('Failed to create event access link');
      }

      return result as { token: string; url: string };
    },
    onSuccess: (data) => {
      // Open the event portal in a new tab
      if (data.url) {
        window.open(data.url, '_blank');
      }

      options.onSuccess?.(data.url);
    },
    onError: (error: Error) => {
      console.error('[useEventToken] Error:', error.message);
      options.onError?.(error);
    },
  });
}
